'use client'

import { useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { cn } from '@/lib/utils'

/**
 * Hardware-style controls for the homepage bench console: a rotary knob, a
 * toggle lever, an analog gauge, an indicator LED and a panel screw. Round
 * parts are SVG circles or clip-path circles, so they stay on the design's
 * square radius everywhere else.
 */

export function Led({ on, className }: { on: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 10 10"
      aria-hidden
      className={cn('size-2.5 shrink-0', className)}
      style={on ? { filter: 'drop-shadow(0 0 4px var(--accent))' } : undefined}
    >
      <circle cx="5" cy="5" r="4.5" className={on ? 'fill-accent' : 'fill-background/20'} />
    </svg>
  )
}

/** A status lamp with a third state: standby breathes a dim cream. */
export function Lamp({ state, className }: { state: 'on' | 'standby' | 'off'; className?: string }) {
  return (
    <svg
      viewBox="0 0 10 10"
      aria-hidden
      className={cn('size-2.5 shrink-0', state === 'standby' && 'lab-breathe', className)}
      style={state === 'on' ? { filter: 'drop-shadow(0 0 4px var(--accent))' } : undefined}
    >
      <circle
        cx="5"
        cy="5"
        r="4.5"
        className={cn(
          'transition-colors duration-500',
          state === 'on' ? 'fill-accent' : state === 'standby' ? 'fill-background/75' : 'fill-background/20'
        )}
      />
    </svg>
  )
}

export function Screw({ className, angle = 35 }: { className?: string; angle?: number }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden className={cn('pointer-events-none absolute size-3', className)}>
      <circle cx="6" cy="6" r="5.5" className="fill-muted-foreground stroke-foreground" strokeWidth="1" />
      <path d="M2.5 6 H9.5" className="stroke-foreground" strokeWidth="1.6" transform={`rotate(${angle} 6 6)`} />
    </svg>
  )
}

const polar = (deg: number, r: number, cx = 50, cy = 50) => {
  const a = (deg * Math.PI) / 180
  return `${(cx + r * Math.sin(a)).toFixed(2)} ${(cy - r * Math.cos(a)).toFixed(2)}`
}

interface KnobProps {
  label: string
  value: number
  min: number
  max: number
  step?: number
  /** Clicking past the last detent goes back to the first. */
  wrap?: boolean
  valueText: string
  onChange: (value: number) => void
  className?: string
}

/** Turn by dragging (sideways or up/down), clicking, or the arrow keys. */
export function RotaryKnob({ label, value, min, max, step = 1, wrap, valueText, onChange, className }: KnobProps) {
  const drag = useRef<{ x: number; y: number; start: number; moved: boolean } | null>(null)
  const range = max - min
  const ratio = range ? (value - min) / range : 0
  const angle = -135 + 270 * ratio
  const detents = Math.round(range / step) + 1
  const discrete = detents <= 12
  const tickCount = discrete ? detents : 11

  const set = (raw: number) => {
    const snapped = Math.round((raw - min) / step) * step + min
    const next = Math.min(max, Math.max(min, Number(snapped.toFixed(4))))
    if (next !== value) onChange(next)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const big = discrete ? step : step * 4
    const moves: Record<string, number> = {
      ArrowUp: step,
      ArrowRight: step,
      ArrowDown: -step,
      ArrowLeft: -step,
      PageUp: big,
      PageDown: -big,
    }
    if (e.key in moves) {
      e.preventDefault()
      set(value + moves[e.key])
    } else if (e.key === 'Home') {
      e.preventDefault()
      set(min)
    } else if (e.key === 'End') {
      e.preventDefault()
      set(max)
    }
  }

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { x: e.clientX, y: e.clientY, start: value, moved: false }
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d) return
    const delta = e.clientX - d.x - (e.clientY - d.y)
    if (Math.abs(delta) > 4) d.moved = true
    if (!d.moved) return
    set(discrete ? d.start + Math.round(delta / 28) * step : d.start + (delta / 160) * range)
  }
  const onPointerUp = () => {
    const d = drag.current
    drag.current = null
    if (!d || d.moved) return
    const next = value + step
    set(next > max ? (wrap ? min : max) : next)
  }

  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const deg = -135 + (270 * i) / (tickCount - 1)
    return `M${polar(deg, 48)} L${polar(deg, i % 5 === 0 || discrete ? 42 : 44.5)}`
  }).join(' ')

  return (
    <div className={cn('flex shrink-0 flex-col items-center gap-1.5', className)}>
      <div
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (drag.current = null)}
        className="group relative size-[5.25rem] cursor-grab touch-none select-none outline-none active:cursor-grabbing"
      >
        <svg viewBox="0 0 100 100" aria-hidden className="absolute inset-0 h-full w-full">
          <path d={ticks} className="stroke-background/45" strokeWidth="2" fill="none" />
          <path d={`M${polar(angle, 48)} L${polar(angle, 41)}`} className="stroke-accent" strokeWidth="3.5" />
          <circle
            cx="50"
            cy="50"
            r="38"
            fill="none"
            strokeWidth="2.5"
            className="stroke-transparent transition-colors group-focus-visible:stroke-accent"
          />
        </svg>
        <div className="lab-knob-shadow absolute inset-[16%]">
          <div
            className="lab-turn absolute inset-0 transition-transform duration-300 group-active:scale-[0.97]"
            style={{ transform: `rotate(${angle}deg)` }}
          >
            <div className="lab-round lab-knurl absolute inset-0" />
            <div className="lab-round lab-knob absolute inset-[13%]" />
            <div className="absolute left-1/2 top-[9%] h-[30%] w-[7%] -translate-x-1/2 bg-accent" />
          </div>
        </div>
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-background">{label}</p>
      <p className="max-w-24 truncate text-xs tabular-nums text-background/60">{valueText}</p>
    </div>
  )
}

interface LeverProps {
  label: string
  on: boolean
  offLabel: string
  onLabel: string
  onToggle: () => void
}

/** A mechanical toggle lever that throws left (off) or right (on). */
export function Lever({ label, on, offLabel, onLabel, onToggle }: LeverProps) {
  return (
    <div className="flex shrink-0 flex-col items-center gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`${label}: ${on ? onLabel : offLabel}`}
        onClick={onToggle}
        className="group relative size-[5.25rem] outline-none"
      >
        <svg viewBox="0 0 64 64" aria-hidden className="h-full w-full">
          <rect x="7" y="31" width="50" height="22" className="fill-foreground stroke-background/15" strokeWidth="1" />
          <rect x="15" y="40" width="34" height="5" className="fill-background/10" />
          <g className="lab-lever" style={{ transform: `rotate(${on ? 34 : -34}deg)` }}>
            <path d="M32 43 L32 15" className="stroke-muted-foreground" strokeWidth="5" strokeLinecap="round" />
            <path d="M32 43 L32 15" className="stroke-background/40" strokeWidth="1.5" strokeLinecap="round" />
            <circle
              cx="32"
              cy="13"
              r="6.5"
              className={cn('transition-colors duration-300', on ? 'fill-accent' : 'fill-muted-foreground')}
            />
          </g>
          <circle cx="32" cy="43" r="6" className="fill-muted-foreground stroke-foreground" strokeWidth="1.5" />
          <rect
            x="1"
            y="1"
            width="62"
            height="62"
            fill="none"
            strokeWidth="2"
            className="stroke-transparent transition-colors group-focus-visible:stroke-accent"
          />
        </svg>
      </button>
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-background">{label}</p>
      <p className="flex gap-1.5 text-xs">
        <span className={on ? 'text-background/45' : 'text-accent'}>{offLabel}</span>
        <span aria-hidden className="text-background/30">/</span>
        <span className={on ? 'text-accent' : 'text-background/45'}>{onLabel}</span>
      </p>
    </div>
  )
}

interface GaugeProps {
  label: string
  value: number
  max: number
  unit?: string
  on: boolean
}

/** An analog dial whose needle springs to the value (and drops when off). */
export function Gauge({ label, value, max, unit, on }: GaugeProps) {
  const ratio = on ? Math.min(1, value / max) : 0
  const angle = -120 + 240 * ratio
  const arc = `M${polar(-120, 36, 50, 52)} A36 36 0 1 1 ${polar(120, 36, 50, 52)}`
  const ticks = Array.from({ length: 9 }, (_, i) => {
    const deg = -120 + i * 30
    return `M${polar(deg, 43, 50, 52)} L${polar(deg, i % 2 ? 40 : 38, 50, 52)}`
  }).join(' ')

  return (
    <div className="flex min-w-0 flex-col items-center">
      <svg viewBox="0 0 100 84" aria-hidden className="w-full max-w-28">
        <circle cx="50" cy="52" r="47" className="fill-foreground stroke-background/15" strokeWidth="1.5" />
        <path d={arc} fill="none" strokeWidth="5" className="stroke-background/15" />
        <path
          d={arc}
          fill="none"
          strokeWidth="5"
          pathLength={100}
          strokeDasharray={`${(ratio * 100).toFixed(1)} 100`}
          className="stroke-accent transition-[stroke-dasharray] duration-700"
        />
        <path d={ticks} fill="none" strokeWidth="1.5" className="stroke-background/50" />
        <g className="lab-needle" style={{ transform: `rotate(${angle}deg)` }}>
          <path d="M50 58 L50 21" strokeWidth="2.5" strokeLinecap="round" className="stroke-primary" />
        </g>
        <circle cx="50" cy="52" r="4.5" className="fill-muted-foreground stroke-foreground" strokeWidth="1" />
      </svg>
      <p className="-mt-2 font-heading text-lg font-bold leading-none tabular-nums text-background">
        {value}
        {unit && <span className="ml-0.5 text-xs font-normal text-background/60">{unit}</span>}
      </p>
      <p className="mt-1 text-xs uppercase tracking-[0.12em] text-background/60">{label}</p>
    </div>
  )
}
